import React, { useCallback, useEffect, useRef } from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { SEO as SEOComponent } from "components/common";
import { startCase } from "lodash-es";

import HrefLang from "../common/HrefLang";
import { ProductDetails } from "./ProductDetails";
import ProductDetailsProvider from "./ProductDetailsProvider";
import ProductUtils from "./utils/ProductUtils";
import { useProductsBySlug } from "@/Apollo/hooks/useProductsBy";
import { useAuth } from "@/auth";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import {
  buildProductSchema,
  serializeJsonLd,
} from "@/common/utils/json-ld.utils";
import { buildProductImageAlt } from "@/common/utils/product-image.utils";
import {
  buildProductPageDescription,
  buildProductPageTitleBase,
} from "@/common/utils/product-seo.utils";
import { isProductAtSlug } from "@/components/ProductDetail/utils/product-slug.utils";
import { useProductProBuyerPrice } from "@/features/pro-buyer-price/hooks/useProBuyerPrice";
import { usMarketVariables } from "@/features/us-market/us-shippable";
import useTrackEvent from "@/hooks/useTrackEvent";
import { useContentLocale } from "@/i18n/hooks";

type ProductDetailsContainerProps = {
  /**
   * Product JSON-LD built in `getServerSideProps` from the product it
   * fetched. When present it is the only source for the schema, so the
   * server HTML carries it whatever the Apollo cache does during render,
   * and the client emits the identical string on hydration.
   */
  productJsonLd?: ReturnType<typeof buildProductSchema>;
  /**
   * WHO-5684: the server fetched the listing without the viewer so the HTML
   * could be cached at the CDN. A signed-in visitor then re-reads it with
   * their token after hydration (favourite, own-listing state); an anonymous
   * visitor keeps the hydrated copy, which is already theirs.
   */
  anonymousHtml?: boolean;
};

export const ProductDetailsContainer = ({
  productJsonLd,
  anonymousHtml = false,
}: ProductDetailsContainerProps) => {
  const { t } = useTranslation("pdp");
  const [trackEvent] = useTrackEvent();
  const { token } = useAuth();

  const router = useRouter();
  const { slug } = router.query;
  const { locale, defaultLocale } = router;
  const contentLocale = useContentLocale();

  const { data, previousData } = useProductsBySlug({
    // `usMarket`: WHO-6510's notice, asked for on /en-us only; the same variables as the server's.
    variables: {
      slug,
      lang: contentLocale.toUpperCase(),
      ...usMarketVariables(locale),
    },
    fetchPolicy: anonymousHtml && token ? "cache-and-network" : undefined,
    onError: (ex: Error) => {
      console.error("ProductDetailsPage:useProductsBySlug", ex.message);
    },
  });

  // While the page asks again in the buyer's display currency (WHO-5142), keep showing the
  // product the server rendered rather than an empty page. Only this product, though: going from
  // product A to product B, A must not render at B's URL with Add to cart and Bid bound to A.
  const previousProduct = previousData?.productBySlug;
  const { productBySlug: product } =
    data ??
    (previousProduct && isProductAtSlug(previousProduct, slug)
      ? previousData
      : undefined) ??
    {};

  analyticsService.useExecAfterMount(
    useCallback(() => {
      // Trigger `view` event. This updates the view count in the database.
      if (product?.id) {
        trackEvent({
          variables: {
            entity: "PRODUCT",
            event: "VIEW",
            id: product?.id,
          },
        });
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product?.id])
  );

  /*
   * `view_item` waits until the Pro Buyer answer is final, so it fires once
   * per listing: the regular price as its value, plus `pro_buyer=true` when
   * this viewer was shown a Pro Buyer price (WHO-5743).
   */
  const proBuyer = useProductProBuyerPrice(
    product?.isOwner ? null : product?.id
  );
  const viewItemTrackedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!product?.id || !proBuyer.settled) {
      return;
    }

    if (viewItemTrackedFor.current === product.id) {
      return;
    }

    viewItemTrackedFor.current = product.id;
    analyticsService.trackViewItem(product, {
      proBuyer: proBuyer.price !== null,
    });
  }, [product, proBuyer.settled, proBuyer.price]);

  // Prefer the server-built schema; the client build only covers renders
  // that reached this component without the page's props.
  const productRichSnippet =
    productJsonLd ??
    ProductUtils.getRichSnippet(product, locale, defaultLocale);

  // Locale-specific title and description (product-seo.utils). `SEO`
  // appends " | Whoppah" itself, so the base form goes in. Before the
  // product resolves there is nothing to differentiate, so the slug is
  // the title and the old description stays as the placeholder.
  const productTitle = product
    ? buildProductPageTitleBase(product, locale)
    : startCase(slug as string);

  const productDescription = product
    ? buildProductPageDescription(product, locale)
    : t("seo_product_page_description", { product: productTitle, price: "" });

  return (
    <>
      <SEOComponent
        title={productTitle}
        description={productDescription}
        image={product?.images?.[0]?.url}
        imageAlt={buildProductImageAlt(product, 0, 0, locale)}
      >
        {productRichSnippet ? (
          <script
            key="product-jsonld"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: serializeJsonLd(productRichSnippet),
            }}
          />
        ) : null}
      </SEOComponent>
      {product ? (
        <HrefLang
          urlMap={{
            en: `/products/${product.slug_en}`,
            nl: `/producten/${product.slug_nl}`,
            de: `/produkte/${product.slug_de}`,
            fr: `/produits/${product.slug_fr}`,
            es: `/productos/${product.slug_es}`,
            it: `/prodotti/${product.slug_it}`,
          }}
        />
      ) : null}
      <ProductDetailsProvider>
        <ProductDetails product={product} />
      </ProductDetailsProvider>
    </>
  );
};
